/**
 * markup.ts — self-describing tile markup for the web app.
 *
 * Why this exists: the accessibility tree of a metric strip used to be one
 * flat text run ("… n/a DSCR 0% LTV 5 Properties", "Insurance −$3,565
 * Property mgmt −$2,040 …"). Assistive tech and text-based audits lose
 * the label↔value pairing and read the LTV / Properties / Property-mgmt /
 * Parks values as blank. Every builder here wraps label + value in ONE
 * element that carries an accessible name of the form "Label: value", and
 * every value is text (shared/tiles.ts — "n/a" when missing).
 *
 * `esc` is the caller's HTML escaper (web ui.escapeHtml); tests pass a
 * trivial one so these stay DOM-free and runnable under node --test.
 */
import type { PortfolioPill } from './portfolioPills.js';
import { distanceTile } from './tiles.ts';

export type Escape = (s: string | number | null | undefined) => string;

/** Accessible name shared by every tile: "Label: value". */
export function tileName(label: string, value: string): string {
  return `${label}: ${value}`;
}

export function pillHtml(p: PortfolioPill, esc: Escape): string {
  const name = tileName(p.label, p.value);
  return `<div class="portfolio-pill${p.tone ? ' tone-' + p.tone : ''}" data-pill="${p.key}" role="group" aria-label="${esc(name)}" title="${esc(name)}">
       <div class="portfolio-pill-value">${esc(p.value)}</div>
       <div class="portfolio-pill-label">${esc(p.label)}</div>
     </div>`;
}

/** One line of the income + expense breakdown. `src` is a provenance tag
 *  ("county", "self-managed") rendered after the label. */
export function expenseRowHtml(
  label: string,
  value: string,
  esc: Escape,
  opts: { src?: string | null; tone?: 'neg' | 'pos' | ''; sub?: boolean } = {},
): string {
  const name = tileName(label, value);
  const cls = `inv-deal-row${opts.sub === false ? '' : ' inv-deal-row-sub'}`;
  const srcHtml = opts.src ? ` <em class="inv-deal-row-src">${esc(opts.src)}</em>` : '';
  const valueCls = opts.tone ? ` class="${opts.tone}"` : '';
  return `<div class="${cls}" role="group" aria-label="${esc(name)}"><span>${esc(label)}${srcHtml}</span><span${valueCls} data-value>${esc(value)}</span></div>`;
}

export interface AmenityLike {
  icon?: string | null;
  label?: string | null;
  detail?: string | null;
  distance?: unknown;
}

/** Nearby-amenity row: label, detail, distance — distance is always text. */
export function amenityRowHtml(a: AmenityLike, esc: Escape): string {
  const label = String(a.label || 'Nearby');
  const detail = String(a.detail || '');
  const dist = distanceTile(a.distance);
  const name = `${label}${detail ? ` (${detail})` : ''}: ${dist}`;
  return `
          <div class="niq-amenity-row" role="listitem" aria-label="${esc(name)}">
            <div class="niq-amenity-icon" aria-hidden="true"><i data-feather="${esc(String(a.icon || 'map-pin'))}" width="14"></i></div>
            <div class="niq-amenity-info">
              <span class="niq-amenity-label">${esc(label)}</span>
              <span class="niq-amenity-detail">${esc(detail)}</span>
            </div>
            <span class="niq-amenity-dist" data-value>${esc(dist)}</span>
          </div>`;
}

/** Market-details stat tile (Sale-to-list, Avg DOM, …). */
export function marketStatHtml(label: string, value: string, esc: Escape): string {
  const name = tileName(label, value);
  return `<div class="inv-market-stat" role="group" aria-label="${esc(name)}">
        <div class="inv-market-stat-value">${esc(value)}</div>
        <div class="inv-market-stat-label">${esc(label)}</div>
      </div>`;
}
